// Scripted API scenarios for the bktestpage harness (PLAN-SELLER-PLAN-PAYBACK-MILESTONE Phase 4a).
// Response bodies mirror docs/fixtures/seller-plan-payback-v1.json; the lease rules mirror the R4
// contract. Nothing here talks to a server, and no value is a real lease token or secret.

export const FIXTURE_UUID = 'f1e3048d-ccb8-5d19-b143-1dd180a24a24';
export const HISTORY_UUID = 'a16008be-492b-5efc-98b0-cd7956d35282';
const HELP_PATH = '/help/subscription-payback-milestone';

const CLEAN_COVERAGE = { manualInvoicePayments: 0, omittedUndatedOrders: 0, conservativeRefunds: 0 };
const NOTED_COVERAGE = { manualInvoicePayments: 2, omittedUndatedOrders: 1, conservativeRefunds: 1 };

export function buildMilestone(overrides = {}) {
  const salesCents = overrides.salesCents ?? 4001;
  return {
    uuid: FIXTURE_UUID,
    cycleId: FIXTURE_UUID,
    startsAt: '2028-02-01T00:00:00.000Z',
    endsAt: '2028-03-01T00:00:00.000Z',
    earnedAt: '2028-02-15T12:00:00.000Z',
    acknowledgedAt: null,
    calculationVersion: 1,
    comparisonRateBps: 2500,
    salesCents,
    subscriptionPaidCents: 1000,
    comparisonFees: { centsNumerator: salesCents, denominator: 4 },
    currency: 'USD',
    coverage: CLEAN_COVERAGE,
    isActiveCycle: true,
    claimable: true,
    ...overrides,
  };
}

export function buildPayback({ availability = 'earned', reason = null, milestone = null, schemaVersion = 1 } = {}) {
  return { schemaVersion, availability, reason, milestone, links: { helpPath: HELP_PATH } };
}

const acknowledged = (overrides = {}) => buildMilestone({
  acknowledgedAt: '2028-02-16T09:30:00.000Z',
  claimable: false,
  ...overrides,
});

const history = () => buildMilestone({
  uuid: HISTORY_UUID,
  cycleId: HISTORY_UUID,
  startsAt: '2028-01-01T00:00:00.000Z',
  endsAt: '2028-02-01T00:00:00.000Z',
  earnedAt: '2028-01-20T08:00:00.000Z',
  acknowledgedAt: '2028-01-21T10:00:00.000Z',
  isActiveCycle: false,
  claimable: false,
});

/** Scenario catalogue shown by the harness. `live` is handled by the harness itself. */
export const SCENARIOS = [
  { key: 'live', label: 'Live API (your channel)',
    hint: 'Real requests. Needs the internal override on your channel and an earned cycle.' },
  { key: 'pending', label: 'Pending popup',
    hint: 'Earned and unacknowledged. The popup opens; closing it acknowledges and the card updates.' },
  { key: 'pendingCoverage', label: 'Pending popup with coverage notes',
    hint: 'Two manual invoice payments, one undated order, one conservative refund.' },
  { key: 'boundaryNotEarned', label: 'Boundary: sales $40.00 on a $10.00 payment',
    hint: 'Equality does not qualify. Nothing new shows.' },
  { key: 'boundaryEarned', label: 'Boundary: sales $40.01 on a $10.00 payment',
    hint: 'Qualifies. The detail shows the exact $10.0025 comparison.' },
  { key: 'acknowledged', label: 'Already acknowledged (card only)',
    hint: 'Active cycle, acknowledged on another device. No popup, card stays.' },
  { key: 'history', label: 'History after cycle end (card only)',
    hint: 'Latest earned snapshot from a past cycle, labelled with its dates.' },
  { key: 'notEarned', label: 'Nonqualifier', hint: 'No popup, no card, no progress nudge.' },
  { key: 'unavailable', label: 'Unavailable (no verified cycle)', hint: 'Shows nothing.' },
  { key: 'unsupportedVersion', label: 'Unsupported schema version', hint: 'Treated as absent.' },
  { key: 'olderServer', label: 'Older server (no payback field)', hint: 'Treated as absent.' },
  { key: 'readError', label: 'Read error (HTTP 500)', hint: 'Shows nothing and does not loop.' },
  { key: 'leaseContended', label: 'Lease contended',
    hint: 'Another device holds the lease. No popup here, card still shows.' },
  { key: 'leaseExpired', label: 'Lease lost while open',
    hint: 'Popup opens, then the first renewal (about 5 s) says another device owns it. It closes silently.' },
  { key: 'ackFailure', label: 'Acknowledgement fails twice',
    hint: 'Close the popup. It stays closed while the ack retries after 2 s and 8 s.' },
  { key: 'ackSuperseded', label: 'Acknowledge after lease superseded',
    hint: 'Close the popup. The ack answers 409 lease-superseded and nothing reopens.' },
  { key: 'crossChannel', label: 'Late response from another channel',
    hint: 'Press "Switch to channel B" within 3 s. Channel A\'s late earned response must be dropped.' },
];

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

function reply(status, body, headers = {}) {
  const lower = Object.fromEntries(Object.entries(headers).map(([k, v]) => [k.toLowerCase(), v]));
  return {
    ok: status >= 200 && status < 300,
    status,
    headers: { get: name => lower[String(name).toLowerCase()] ?? null },
    clone() { return reply(status, body, headers); },
    json: async () => JSON.parse(JSON.stringify(body)),
  };
}

const refuse = (status, reason, extra = {}, headers = {}) => reply(
  status,
  { meta: { success: false, reason, ...extra }, error: 'Not displayed by clients' },
  headers,
);

/**
 * A stateful fake of the plan-billing endpoints for one scenario and one channel. Returns the
 * `fetch` the client calls plus nothing that could carry a secret. `onCall({ at, call, status })`
 * lets the harness show a request log.
 */
export function createScriptedFetch(scenario, { channel = 'A', onCall = () => {}, crossChannelDelayMs = 3000 } = {}) {
  const st = { acknowledged: false, token: null, serial: 0, acks: 0 };
  const newToken = () => `harnessLeaseToken`.padEnd(40, 'x') + String(++st.serial).padStart(3, '0');
  const activeMilestone = () => buildMilestone(scenario === 'pendingCoverage'
    ? { salesCents: 12345, coverage: NOTED_COVERAGE }
    : { salesCents: 4001, coverage: CLEAN_COVERAGE });

  const paybackFor = () => {
    switch (scenario) {
      case 'boundaryNotEarned':
      case 'notEarned':
        return buildPayback({ availability: 'not-earned' });
      case 'unavailable':
        return buildPayback({ availability: 'unavailable', reason: 'no-active-cycle' });
      case 'unsupportedVersion':
        return buildPayback({ schemaVersion: 2, milestone: activeMilestone() });
      case 'acknowledged':
        return buildPayback({ milestone: acknowledged() });
      case 'history':
        return buildPayback({ availability: 'not-earned', milestone: history() });
      case 'crossChannel':
        return channel === 'A'
          ? buildPayback({ milestone: activeMilestone() })
          : buildPayback({ availability: 'not-earned' });
      default:
        return buildPayback({
          milestone: st.acknowledged ? acknowledged({ acknowledgedAt: '2028-02-20T00:00:00.000Z' }) : activeMilestone(),
        });
    }
  };

  const leaseBody = (outcome, token, heartbeatSeconds) => reply(200, {
    meta: { success: true, outcome },
    data: {
      payback: paybackFor(),
      lease: {
        token,
        expiresAt: new Date(Date.now() + 120000).toISOString(),
        ttlSeconds: 120,
        heartbeatSeconds,
      },
    },
  });

  const handle = async (url, options) => {
    const method = options?.method || 'GET';
    const body = options?.body ? JSON.parse(options.body) : {};
    const action = String(url).match(/\/payback\/[^/]+\/(claim|acknowledge|release)$/)?.[1];

    if (!action) {
      if (scenario === 'readError') return reply(500, { error: 'Failed to fetch plan billing' });
      if (scenario === 'crossChannel' && channel === 'A') await sleep(crossChannelDelayMs);
      if (scenario === 'olderServer') return reply(200, { data: {}, meta: {} });
      return reply(200, { data: { payback: paybackFor() }, meta: { limit: 1, total: 0 } });
    }

    if (action === 'claim') {
      if (scenario === 'leaseContended') {
        return refuse(409, 'contended', { retryAfterSeconds: 87 }, { 'Retry-After': '87' });
      }
      if (st.acknowledged) return refuse(409, 'acknowledged');
      const heartbeat = scenario === 'leaseExpired' ? 5 : 45;
      if (body.leaseToken) {
        if (body.leaseToken !== st.token) return refuse(409, 'contended', { retryAfterSeconds: 60 });
        if (scenario === 'leaseExpired') return refuse(409, 'contended', { retryAfterSeconds: 60 });
        return leaseBody('renewed', st.token, heartbeat);
      }
      st.token = newToken();
      return leaseBody('claimed', st.token, heartbeat);
    }

    if (action === 'acknowledge') {
      st.acks += 1;
      if (scenario === 'ackFailure' && st.acks <= 2) throw new TypeError('Network request failed');
      if (scenario === 'ackSuperseded') {
        st.acknowledged = true;
        return refuse(409, 'lease-superseded');
      }
      if (st.acknowledged) {
        return reply(200, { meta: { success: true, outcome: 'already-acknowledged' }, data: { payback: paybackFor() } });
      }
      if (body.leaseToken !== st.token) return refuse(409, 'lease-superseded');
      st.acknowledged = true;
      st.token = null;
      return reply(200, { meta: { success: true, outcome: 'acknowledged' }, data: { payback: paybackFor() } });
    }

    // release
    if (body.leaseToken && body.leaseToken === st.token && !st.acknowledged) {
      st.token = null;
      return reply(200, { meta: { success: true, outcome: 'released' }, data: { payback: paybackFor() } });
    }
    return reply(200, { meta: { success: true, outcome: 'not-holder' }, data: { payback: paybackFor() } });
  };

  return {
    fetch: async (url, options) => {
      const call = `${options?.method || 'GET'} ${String(url).replace(/^https?:\/\/[^/]+\/v1\/seller\/payments\/plan-billing/, '')
        .replace(/\/payback\/[^/]+/, '/payback/:uuid') || '/'}`;
      try {
        const response = await handle(url, options);
        onCall({ at: new Date().toISOString().slice(11, 19), call, status: response.status });
        return response;
      } catch (error) {
        onCall({ at: new Date().toISOString().slice(11, 19), call, status: 'network error' });
        throw error;
      }
    },
  };
}
